<template>
    <div class="py-12">
        <inertia-link :href="route('kiosk.app-env-vars.index')">
            <AtomicButton color="yellow" size="sm">
                {{ $t("vue.back_to_app_env_vars") }}
                <template #icon>
                    <BackspaceIcon
                        class="mr-2 h-6 w-6 transition duration-200 group-hover:text-gray-600"
                    />
                </template>
            </AtomicButton>
        </inertia-link>

        <h1
            class="mt-2 text-2xl font-medium leading-normal tracking-small text-gray-800"
        >
            {{ $t("vue.create_new_app_env_var") }}
        </h1>

        <form class="flex flex-col" @submit.prevent="saveAppEnvVar">
            <TextInput
                v-model="v$.form.key.$model"
                class="my-6 w-52 sm:w-80"
                :label="$t('vue.key')"
                type="text"
                :placeholder="$t('vue.max_255_characters')"
                :error="getError('key')"
            />

            <SelectInput
                v-model="state.form.value_type"
                class="mb-6 w-52 sm:w-80"
                :data="typeOptions"
                :label="$t('vue.value_type')"
                :placeholder="$t('vue.value_type')"
                :error="getError('value_type')"
            />

            <TextInput
                v-model="v$.form.value.$model"
                class="mb-6 w-52 sm:w-80"
                :label="$t('vue.value')"
                type="text"
                :error="getError('value')"
            />

            <AtomicButton
                type="submit"
                color="yellow"
                size="sm"
                class="mt-4 w-max"
                :disabled="loading || v$.$invalid"
            >
                {{ $t("vue.save") }}
            </AtomicButton>
        </form>
    </div>
</template>

<script lang="ts" setup>
import useVuelidate from "@vuelidate/core";
import { BackspaceIcon } from "@heroicons/vue/24/outline";
import { required, maxLength } from "@vuelidate/validators";
import route from "ziggy-js";
import { router } from "@inertiajs/vue3";
import type IInputErrors from "@/types/Atomic/IInputErrors";
import type { AppEnvVarValueType } from "@/types/AppEnvVars/IAppEnvVar";

const props = defineProps<{
    valueTypes: AppEnvVarValueType[];
}>();

const typeOptions = props.valueTypes.map((type) => ({
    title: type,
    value: type,
}));

const state = reactive({
    form: {
        key: "",
        value: "",
        value_type: props.valueTypes[0] ?? "string",
    },
});

const rules = {
    form: {
        key: {
            required,
            maxLength: maxLength(255),
        },
        value: {
            required,
            maxLength: maxLength(65535),
        },
        value_type: {
            required,
        },
    },
};

const v$ = useVuelidate(rules, state);
const loading = ref(false);
const errors = ref<IInputErrors>({});

const saveAppEnvVar = () => {
    v$.value.$touch();
    if (v$.value.form.$error) {
        return;
    }

    loading.value = true;
    router.post(route("kiosk.app-env-vars.store"), state.form, {
        preserveScroll: false,
        onError: (err) => {
            errors.value = err;
        },
        onSuccess: () => {
            errors.value = {};
        },
        onFinish: () => {
            loading.value = false;
        },
    });
};

const getError = (field: string) => {
    return (
        v$.value.form[field]?.$errors[0]?.$message ||
        errors.value?.[field] ||
        ""
    );
};
</script>
